import { Button, Link } from '@/Components/UI/Button/Button';
import {
  GIGASHEET_API_SUPPORT,
  LINK_ENTERPRISE_DEMO,
  LINK_PRODUCT_UPSELL_ENTERPRISE_DEMO,
} from '@/Utils/constants';
import { manageBilling } from '@/helpers/settingsHelper';

export const UPGRADE_TYPE_JSON_ANALYST = 'jsonAnalyst';

export const teamMemberUpgrade = () => (
  <div className='h-10 px-6 text-sm text-ui-secondary'>
    Contact your team’s administrator to request an upgrade for access.
  </div>
);

export const premiumFeatureUpgradeStorage = (usedStorage, maxStorageSize) => (
  <div className='h-16 px-6 text-sm text-ui-secondary'>
    <span className='italic font-bold'>
      You’re using {usedStorage} of storage which exceeds your account storage limit of{' '}
      {maxStorageSize}.
    </span>{' '}
    Upgrade to an account with more storage or delete some files from your Library to restore full
    functionality.
  </div>
);

export const premiumFeatureCombineFiles = () => (
  <div className='h-16 px-6 text-sm text-ui-secondary'>
    <span className='italic font-bold'>
      Files cannot be combined as your account’s storage limits have been exceeded.
    </span>{' '}
    Upgrade to an account with more storage or delete some files from your Library to restore full
    functionality.
  </div>
);

export const premiumFeatureSheetPreview = (currentFileSize, maxStorageSize) => (
  <div className='h-16 px-6 text-sm text-ui-secondary'>
    <span className='italic font-bold'>
      This file is {currentFileSize} and is restricted to a limited preview as it exceeds your
      account storage limit of {maxStorageSize}.
    </span>{' '}
    For full sheet control, upgrade to an account with a higher storage limit or delete some files
    from your Library.
  </div>
);

export const fileExceedsRowLimit = (currentFileRowsFormatted, maxFileRowsFormatted) => (
  <div className='text-ui-secondary font-bold mb-4 pl-4'>
    Limited Preview: This file is {currentFileRowsFormatted} rows and exceeds account row limit of{' '}
    {maxFileRowsFormatted} rows.
  </div>
);

export const premiumFeatureFooter = (onClose) => (
  <div className='flex justify-end w-full h-12 px-7'>
    <Button onClick={onClose} variant='ghost'>
      Dismiss
    </Button>
  </div>
);

export const generalUpgradeFooter = (
  hasBillingId,
  manageBillingModal,
  requestTrial,
  upgradeType = 'default'
) => {
  return hasBillingId ? (
    <div className='flex flex-row justify-end'>
      <div className='h-16 w-[700px] flex justify-center gap-48'>
        <Button onClick={manageBilling} className='w-40' color='midnight'>
          Manage Subscription
        </Button>
        <Link
          to={LINK_ENTERPRISE_DEMO}
          target='__blank'
          size='large'
          onClick={requestTrial}
          className='flex items-center justify-center w-32 h-8 px-3 text-sm rounded !text-ui bg-sunrise-500 hover:bg-sunrise-600 active:bg-sunrise-700'
        >
          Request Trial
        </Link>
      </div>
    </div>
  ) : (
    <div className='flex flex-col items-center'>
      <div className='flex flex-row justify-center w-full gap-56 h-14'>
        <div className='font-semibold text-center w-28 text-midnight-500'>Your Plan</div>
        <Button onClick={manageBillingModal} className='w-28' color='sunrise'>
          Upgrade Now
        </Button>
        <Link
          to={
            upgradeType === UPGRADE_TYPE_JSON_ANALYST
              ? LINK_PRODUCT_UPSELL_ENTERPRISE_DEMO
              : LINK_ENTERPRISE_DEMO
          }
          target='__blank'
          size='large'
          onClick={requestTrial}
          className='flex items-center justify-center w-32 h-8 px-3 text-sm rounded !text-ui bg-sunrise-500 hover:bg-sunrise-600 active:bg-sunrise-700'
        >
          {upgradeType === UPGRADE_TYPE_JSON_ANALYST ? 'Book a meeting' : 'Request Trial'}
        </Link>
      </div>
      <div className='h-10'>
        <Link
          to={GIGASHEET_API_SUPPORT}
          size='medium'
          target='__blank'
          className='font-semibold underline'
        >
          Need to generate an API key?
        </Link>
      </div>
    </div>
  );
};

export const GENERAL_UPGRADE_TITLE = 'You could use an upgrade';
export const GENERAL_SHORT_UPGRADE_TITLE = 'Upgrade Required';
export const PAGINATION_VIEW_TITLE = 'View More Rows on the Premium Plan';
export const TEAM_WORKSPACE_TITLE = 'Add Team Workspace on the Business Plan';
export const CASE_SENSITIVE_FIND_AND_REPLACE_TITLE =
  'Add Case Sensitive to Find & Replace on the Premium Plan';
export const MATH_CELL_CONTENT_FIND_AND_REPLACE_TITLE =
  'Add Match Cell Content to Find & Replace on the Premium Plan';
export const SHEET_HISTORY_TITLE = 'Add Sheet History on the Business Plan';
export const PUBLISH_TO_WEB_TITLE = 'Add Publish to Web on the Business Plan';
export const DATABRICKS_EXPORT_TITLE = 'Connect to Databricks on the Enterprise Plan';
export const SNOWFLAKE_EXPORT_TITLE = 'Connect to Snowflake on the Enterprise Plan';
export const HUBSPOT_EXPORT_TITLE = 'Connect to HubSpot on an Enterprise Plan';
export const HUBSPOT_EXPORT_EXISTING_ENTERPRISE_TITLE =
  'Connect to HubSpot on your Enterprise Plan';
export const SPLIT_SHEET_TITLE = 'Add Split to Multiple CSVs on the Premium Plan';
export const TEAM_MEMBER_UPGRADE_TITLE = 'Account Upgrade Required';
export const TEAM_WORKSPACE_UPGRADE_TITLE = 'Add a Team Workspace on the Business Plan';
export const JSON_UPGRADE_TITLE = 'Analyze JSON files with Business and Enterprise Plans';

export const upgradeHandlerByType = (
  hasBillingId = false,
  usedStorage = '',
  currentFileSize = '',
  maxStorageSize = '',
  currentFileRowsFormatted = '',
  maxFileRowsFormatted = '',
  manageBillingModal = () => {},
  requestTrial = () => {},
  onClose = () => {},
  title = null
) => {
  const commonFooter = generalUpgradeFooter(hasBillingId, manageBillingModal, requestTrial);
  const defaultTypes = {
    base: { title: title ? title : GENERAL_UPGRADE_TITLE, align: 'left' },
    general: { title: GENERAL_UPGRADE_TITLE, align: 'center' },
    generalShort: { title: GENERAL_SHORT_UPGRADE_TITLE, align: 'left' },
    paginationView: { title: PAGINATION_VIEW_TITLE, align: 'left' },
    caseSensitiveFindAndReplace: { title: CASE_SENSITIVE_FIND_AND_REPLACE_TITLE, align: 'left' },
    mathCellContentFindAndReplace: {
      title: MATH_CELL_CONTENT_FIND_AND_REPLACE_TITLE,
      align: 'left',
    },
    sheetHistory: { title: SHEET_HISTORY_TITLE, align: 'left' },
    snowflakeExport: { title: SNOWFLAKE_EXPORT_TITLE, align: 'left' },
    databricksExport: { title: DATABRICKS_EXPORT_TITLE, align: 'left' },
    hubspotExport: { title: HUBSPOT_EXPORT_TITLE, align: 'left' },
    hubspotExportExisting: { title: HUBSPOT_EXPORT_EXISTING_ENTERPRISE_TITLE, align: 'left' },
    publishToWeb: { title: PUBLISH_TO_WEB_TITLE, align: 'left' },
    splitSheet: { title: SPLIT_SHEET_TITLE, align: 'left' },
    teamWorkspace: { title: TEAM_WORKSPACE_UPGRADE_TITLE, align: 'left' },
  };

  const mapping = Object.keys(defaultTypes).reduce((acc, key) => {
    acc[key] = {
      ...defaultTypes[key],
      subhead: null,
      footer: commonFooter,
    };
    return acc;
  }, {});

  // Special cases
  mapping.teamMember = {
    title: TEAM_MEMBER_UPGRADE_TITLE,
    align: 'left',
    subhead: teamMemberUpgrade(),
    footer: premiumFeatureFooter(onClose),
  };

  mapping.jsonAnalyst = {
    title: JSON_UPGRADE_TITLE,
    align: 'left',
    subhead: null,
    footer: generalUpgradeFooter(hasBillingId, manageBillingModal, null, UPGRADE_TYPE_JSON_ANALYST),
  };

  mapping.combineFiles = {
    title: GENERAL_UPGRADE_TITLE,
    align: 'left',
    subhead: premiumFeatureCombineFiles(),
    footer: premiumFeatureFooter(onClose),
  };

  mapping.storageSize = {
    title: GENERAL_UPGRADE_TITLE,
    align: 'left',
    subhead: premiumFeatureUpgradeStorage(usedStorage, maxStorageSize),
    footer: premiumFeatureFooter(onClose),
  };

  mapping.fileSize = {
    title: GENERAL_UPGRADE_TITLE,
    align: 'left',
    subhead: premiumFeatureSheetPreview(currentFileSize, maxStorageSize),
    footer: premiumFeatureFooter(onClose),
  };

  mapping.fileExceedsRowLimit = {
    title: GENERAL_SHORT_UPGRADE_TITLE,
    align: 'left',
    subhead: fileExceedsRowLimit(currentFileRowsFormatted, maxFileRowsFormatted),
    footer: commonFooter,
  };

  return mapping;
};
